import { useEffect, useState } from "react";
import { api, apiError, getToken, API } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, DownloadSimple, Trash, FileDoc, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";

const DOC_TYPES = [
  { value: "jobsheet", label: "Jobsheet" },
  { value: "field_report", label: "Field Service Report" },
  { value: "invoice", label: "Invoice" },
  { value: "custom", label: "Custom" },
];
const typeLabel = (v) => DOC_TYPES.find((t) => t.value === v)?.label || v;

export default function Documents() {
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [generated, setGenerated] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [docType, setDocType] = useState("jobsheet");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/document-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/generated-documents").then((r) => setGenerated(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const tplUrl = (t) => `${API}/document-templates/${t.id}/download?auth=${getToken()}`;
  const genUrl = (g) => `${API}/generated-documents/${g.id}/download?auth=${getToken()}`;

  const upload = async () => {
    if (!file) { toast.error("Choose a .docx or .xlsx file"); return; }
    if (!name.trim()) { toast.error("Template name is required"); return; }
    const fd = new FormData();
    fd.append("name", name.trim());
    fd.append("doc_type", docType);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/document-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template uploaded");
      setOpen(false); setName(""); setFile(null); setDocType("jobsheet");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/document-templates/${id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const FmtIcon = ({ fmt }) => fmt === "xlsx"
    ? <FileXls size={18} weight="fill" className="text-green-600" />
    : <FileDoc size={18} weight="fill" className="text-blue-600" />;

  return (
    <>
      <PageHeader title="Documents" subtitle="Upload templates & generate filled documents"
        actions={tab === "templates" && <Button data-testid="upload-template-btn" onClick={() => setOpen(true)} className="rounded-sm gap-2"><Plus size={16} weight="bold" /> Upload Template</Button>} />
      <div className="p-8 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm" data-testid="doc-tabs">
            <TabsTrigger value="templates" className="rounded-sm" data-testid="tab-templates">Templates ({templates.length})</TabsTrigger>
            <TabsTrigger value="generated" className="rounded-sm" data-testid="tab-generated">Generated ({generated.length})</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "templates" && (
          <div className="border border-border rounded-sm bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead>Template</TableHead><TableHead>Type</TableHead>
                  <TableHead>Placeholders</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="templates-table">
                {templates.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No templates yet. Upload a .docx or .xlsx file with placeholders like <code>{"{{customer_name}}"}</code>.</TableCell></TableRow>}
                {templates.map((t) => (
                  <TableRow key={t.id} className="hover:bg-secondary/30">
                    <TableCell>
                      <div className="font-semibold flex items-center gap-2"><FmtIcon fmt={t.file_format} /> {t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.original_filename}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm">{typeLabel(t.doc_type)}</Badge></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {(t.placeholders || []).length === 0 && <span className="text-xs text-muted-foreground">none detected</span>}
                        {(t.placeholders || []).map((p) => <code key={p} className="text-[10px] bg-secondary px-1.5 py-0.5 rounded-sm">{p}</code>)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <a href={tplUrl(t)} target="_blank" rel="noreferrer">
                        <Button variant="ghost" size="sm" className="rounded-sm gap-1" data-testid={`tpl-download-${t.id}`}><DownloadSimple size={15} /></Button>
                      </a>
                      <Button variant="ghost" size="sm" className="rounded-sm text-destructive" onClick={() => remove(t.id)} data-testid={`tpl-delete-${t.id}`}><Trash size={15} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {tab === "generated" && (
          <div className="border border-border rounded-sm bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead>Document</TableHead><TableHead>Service</TableHead>
                  <TableHead>Partner</TableHead><TableHead>Customer</TableHead>
                  <TableHead>Generated</TableHead><TableHead className="text-right">Download</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="generated-table">
                {generated.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No documents generated yet. Generate from the Jobs page.</TableCell></TableRow>}
                {generated.map((g) => (
                  <TableRow key={g.id} className="hover:bg-secondary/30">
                    <TableCell>
                      <div className="font-semibold flex items-center gap-2"><FmtIcon fmt={g.file_format} /> {g.template_name}</div>
                      <div className="text-xs text-muted-foreground">{g.filename}</div>
                    </TableCell>
                    <TableCell className="text-sm">{g.service_type || "—"}</TableCell>
                    <TableCell className="text-sm">{g.partner_name || "—"}</TableCell>
                    <TableCell className="text-sm">{g.customer_name || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(g.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right">
                      <a href={genUrl(g)} target="_blank" rel="noreferrer">
                        <Button variant="ghost" size="sm" className="rounded-sm gap-1" data-testid={`gen-download-${g.id}`}><DownloadSimple size={15} /> Download</Button>
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="template-form">
          <SheetHeader><SheetTitle className="font-heading">Upload Template</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div>
              <Label className="label-caps">Template Name</Label>
              <Input data-testid="tpl-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm mt-1.5" placeholder="e.g. AC Installation Jobsheet" />
            </div>
            <div>
              <Label className="label-caps">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="tpl-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">File (.docx or .xlsx)</Label>
              <Input data-testid="tpl-file" type="file" accept=".docx,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-sm mt-1.5" />
            </div>
            <div className="bg-secondary/60 rounded-sm p-3 text-xs text-muted-foreground leading-relaxed">
              Use placeholders inside the document, e.g. <code className="bg-card px-1 rounded-sm">{"{{customer_name}}"}</code>, <code className="bg-card px-1 rounded-sm">{"{{job_id}}"}</code>, <code className="bg-card px-1 rounded-sm">{"{{service_type}}"}</code>, <code className="bg-card px-1 rounded-sm">{"{{amount}}"}</code>, <code className="bg-card px-1 rounded-sm">{"{{partner_name}}"}</code>, <code className="bg-card px-1 rounded-sm">{"{{address}}"}</code>, <code className="bg-card px-1 rounded-sm">{"{{scheduled_date}}"}</code>, <code className="bg-card px-1 rounded-sm">{"{{today}}"}</code>.
            </div>
          </div>
          <SheetFooter className="sticky bottom-0 bg-card pt-3 -mx-6 px-6 border-t border-border">
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={upload} disabled={busy} data-testid="tpl-save">{busy ? "Uploading…" : "Upload"}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
